import { memo, useCallback } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Badge, Button, SlideOut, SlideOutHeader, Skeleton } from '@aimsos/ui';

import { useReportTruthFactIssue } from '../../../hooks/useReportTruthFactIssue.js';
import { useTruthFact } from '../../../hooks/useTruthFact.js';
import { formatDateWithYear, formatTraitDate } from '../../../utils/traitDates.js';
import TraitDetailSection from '../../intelligence/TraitDetailSection.jsx';
import TraitHistory from '../../intelligence/TraitHistory.jsx';
import TraitUsedIn from '../../intelligence/TraitUsedIn.jsx';
import TraitValidity from '../../intelligence/TraitValidity.jsx';
import ItemUnavailableState from '../../item-slideout/ItemUnavailableState.jsx';
import { ErrorState } from '../../states/index.js';

import { BadgeCheck } from 'lucide-react';

/**
 * One Truth fact in the profile slide-out (CT-5719): what it states, where it
 * was confirmed from, the source's own words, how long it stays valid, where it
 * is used, its history, and "Report an issue".
 *
 * Registered for the `truth` kind of the item slide-out host
 * (`?item=truth:<factId>`). Validity, Where it's used and History are the trait
 * slide-out's own blocks (CT-5715): a fact and a trait age and travel the same
 * way. A fact that no longer exists, or that the viewer may not open, shows the
 * unavailable state.
 */
const TruthFactSlideOut = memo(function TruthFactSlideOut({ itemId, entityType, entityId, entityTitle, onClose, onChanged }) {
  const { t, i18n } = useTranslation('unified-profiles');
  const language = i18n?.language;

  const { fact, loading, error, notFound, accessDenied, refetch, refresh } = useTruthFact({ entityType, entityId, factId: itemId });
  const handleReported = useCallback(() => {
    refresh();
    onChanged?.();
  }, [refresh, onChanged]);
  const { report, reporting, reported } = useReportTruthFactIssue({ entityType, entityId, factId: itemId, onReported: handleReported });

  const title = fact?.type
    ? t('knowledge.truth.detail.title', { type: t(`knowledge.truth.types.${fact.type}`) })
    : t('knowledge.truth.detail.fallbackTitle');

  const renderHeader = useCallback(
    (titleId) => <SlideOutHeader titleId={titleId} title={title} icon={BadgeCheck} size="M" showClose onClose={onClose} />,
    [title, onClose],
  );

  const body = () => {
    if (loading) {
      return (
        <div data-testid="truth-fact-loading" className="flex flex-col gap-3" aria-busy>
          <Skeleton width="30%" height="14px" borderRadius="4px" />
          <Skeleton width="60%" height="20px" borderRadius="4px" />
          <Skeleton width="90%" height="14px" borderRadius="4px" />
        </div>
      );
    }
    if (notFound || accessDenied) return <ItemUnavailableState />;
    if (error) return <ErrorState message={error} onRetry={refetch} />;
    if (!fact) return null;

    const confirmedOn = formatDateWithYear(fact.confirmedAt, language);
    const confirmedShort = formatTraitDate(fact.confirmedAt, language);
    const channel = fact.sourceChannel ? t(`knowledge.truth.channels.${fact.sourceChannel}`) : null;

    return (
      <div data-testid="truth-fact-detail" data-fact-id={fact.id} className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <span className="text-xs text-content-muted">{fact.label}</span>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-lg font-semibold text-content">{fact.value}</span>
            {fact.sourceKind ? (
              <Badge variant="neutral" size="small">
                {t(`knowledge.sourceTypes.${fact.sourceKind}`)}
              </Badge>
            ) : null}
            {fact.status === 'reconfirm_due' ? (
              <Badge variant="warning" size="small">
                {t('knowledge.truth.status.reconfirm_due')}
              </Badge>
            ) : null}
          </div>
          {fact.confirmedSource && confirmedOn ? (
            <p className="text-sm text-content-secondary">
              {t('knowledge.truth.detail.confirmedFrom', { source: fact.confirmedSource, date: confirmedOn })}
            </p>
          ) : null}
        </div>

        <TraitDetailSection title={t('knowledge.truth.detail.details')}>
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
            <dt className="text-content-muted">{t('knowledge.truth.detail.source')}</dt>
            <dd className="text-content">{fact.confirmedSource ?? '—'}</dd>
            <dt className="text-content-muted">{t('knowledge.truth.detail.confirmed')}</dt>
            <dd data-testid="truth-fact-confirmed" className="text-content">
              {confirmedOn ?? '—'}
            </dd>
          </dl>
        </TraitDetailSection>

        {fact.quote ? (
          <TraitDetailSection title={t('knowledge.truth.detail.sourceQuote')}>
            {/* As in the prototype: the channel, then "<channel> · <date>", then its words. */}
            <div data-testid="truth-fact-source" className="flex flex-col gap-2 rounded-lg border border-edge p-3 text-sm">
              {channel ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span data-testid="truth-fact-source-channel">
                    <Badge variant="neutral" size="small">
                      {channel}
                    </Badge>
                  </span>
                  <span className="text-content-secondary">{[channel, confirmedShort].filter(Boolean).join(' · ')}</span>
                </div>
              ) : null}
              <p className="text-content-secondary">“{fact.quote}”</p>
            </div>
          </TraitDetailSection>
        ) : null}

        <TraitDetailSection title={t('intelligence.traits.detail.validity')}>
          <TraitValidity since={fact.since} expiresAt={fact.expiresAt} />
        </TraitDetailSection>

        <TraitDetailSection title={t('intelligence.traits.detail.usedIn')}>
          <TraitUsedIn usedIn={fact.usedIn} />
          {fact.visibility ? (
            <p className="text-sm text-content-secondary">
              {t(`intelligence.traits.detail.visibility.${fact.visibility}`, { name: entityTitle ?? t('profile.untitled') })}
            </p>
          ) : null}
        </TraitDetailSection>

        {fact.history.length ? (
          <TraitDetailSection title={t('intelligence.traits.detail.history')}>
            <TraitHistory history={fact.history} />
          </TraitDetailSection>
        ) : null}
      </div>
    );
  };

  return (
    <SlideOut isOpen onClose={onClose} renderHeader={renderHeader} size="M" ariaLabel={title}>
      <div data-testid="truth-fact-slideout" data-item-id={itemId} className="flex h-full min-h-0 flex-col gap-4">
        <div className="scrollbar-aimsos-auto min-h-0 flex-1 overflow-y-auto">{body()}</div>

        {fact ? (
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-edge pt-4">
            <span className="text-xs text-content-muted">{t('intelligence.traits.detail.reportHint')}</span>
            <Button variant="secondary" size="sm" onClick={report} disabled={reporting || reported || fact.reportOpen}>
              {reported || fact.reportOpen
                ? t('intelligence.traits.detail.reported')
                : t('intelligence.traits.detail.report')}
            </Button>
          </div>
        ) : null}
      </div>
    </SlideOut>
  );
});

export default TruthFactSlideOut;
